import { instanceAuthEnabled } from './instance-auth-flag';
import { ensureInstance } from './instance-auth';
import { instanceHttpFetch } from './instance-http';
import { redirectToLogin } from './auth-redirect';
import { devMockResponse } from './dev-mock-data';

/**
 * Drop-in replacement for `fetch` for same-origin `${API_BASE}/api/*` data calls.
 * One place to switch auth per the `symphonyInstanceAuthWeb` toggle
 * (docs/AUTH-MIDDLEWARE-AUDIT.md §4.1):
 *
 * - flag OFF → today's behavior: a plain credentialed `fetch` (the ungated legacy
 *   path, unchanged for everyone the toggle doesn't target).
 * - flag ON  → the request goes through the Wix http client (`instanceHttpFetch`),
 *   NO raw `fetch` under the gate: `ok` attaches the signed `-666` instance as
 *   `Authorization`; `none` (no metasite yet) still uses the client, credentialed
 *   with no instance; `unauthenticated` → redirect to login; `error` → throw
 *   (never silently proxy a broken instance path).
 *
 * Returns a `Response` in every mode, so a call site just swaps `fetch` → `apiFetch`.
 */
const LOG = '[instance-auth]';

export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  // Dev-only: an entity whose count is being faked from the brand-mark dev menu is answered locally
  // instead of going out. Inert in production and whenever nothing is faked (lib/dev-mock-data.ts).
  const mocked = devMockResponse(input, init);
  if (mocked) return mocked;

  if (!(await instanceAuthEnabled())) {
    return fetch(input, { credentials: 'include', ...init });
  }

  const result = await ensureInstance();
  if (result.status === 'unauthenticated') {
    console.info(`${LOG} apiFetch(${input}) unauthenticated -> login`);
    redirectToLogin();
    // The page is navigating away; block this call rather than erroring the caller.
    return new Promise<Response>(() => {});
  }
  if (result.status === 'error') {
    // Fail loudly — do NOT fall back to the proxy, which would mask a broken
    // instance path behind the middleware.
    console.error(`${LOG} apiFetch(${input}) instance acquisition failed — not falling back`);
    throw new Error(`instance acquisition failed for ${input}`);
  }

  // Under the gate: always the Wix http client. Attach the instance when we have
  // one; on `none` (no metasite yet) go credentialed-only so the proxy still serves.
  const instance = result.status === 'ok' ? result.instance : undefined;
  console.debug(`${LOG} apiFetch(${input}) via http client${instance ? ' + instance' : ''}`);
  return instanceHttpFetch(input, init, instance);
}

/**
 * Streaming counterpart to `apiFetch` for the SSE endpoints (chat `/stream`,
 * `/onboarding/start`), which read the body incrementally via
 * `res.body.getReader()`.
 *
 * `instanceHttpFetch` (`@wix/http-client`, XHR `responseType:'text'`) BUFFERS
 * the whole body, so it cannot stream — streaming must use native `fetch`,
 * which yields a live `ReadableStream`. Auth is otherwise identical to
 * `apiFetch`: under the gate the signed instance rides as an explicit
 * `Authorization` header (never cookie-ambient), so the only difference is the
 * transport, forced by the streaming requirement.
 *
 * - flag OFF → plain credentialed `fetch` (unchanged legacy path).
 * - flag ON  → `ok` fetches with the instance header; `none` (no metasite yet)
 *   fetches credentialed-only; `unauthenticated` → login; `error` → throw.
 *
 * `init` (including `signal` for abort and `body`) is forwarded verbatim.
 */
export async function apiFetchStream(input: string, init: RequestInit = {}): Promise<Response> {
  // Same dev-only interception as `apiFetch`: this is the app's OTHER way out, so a mocked entity would
  // otherwise still reach the server through it. A synthetic id gets the local answer (a 404 for a stream
  // nothing can fake), which the caller handles as any failed stream.
  const mocked = devMockResponse(input, init);
  if (mocked) return mocked;

  if (!(await instanceAuthEnabled())) {
    return fetch(input, { credentials: 'include', ...init });
  }

  const result = await ensureInstance();
  if (result.status === 'unauthenticated') {
    console.info(`${LOG} apiFetchStream(${input}) unauthenticated -> login`);
    redirectToLogin();
    return new Promise<Response>(() => {});
  }
  if (result.status === 'error') {
    console.error(`${LOG} apiFetchStream(${input}) instance acquisition failed — not falling back`);
    throw new Error(`instance acquisition failed for ${input}`);
  }

  const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
  if (result.status === 'ok') headers.Authorization = result.instance;
  console.debug(`${LOG} apiFetchStream(${input}) via fetch (streaming)${result.status === 'ok' ? ' + instance' : ''}`);
  return fetch(input, { credentials: 'include', ...init, headers });
}
